import { ref, uploadBytesResumable, deleteObject } from "firebase/storage";
import { v4 as uuidv4 } from "uuid";
import { StorageDB } from "utils/firebase";

export const normalizeAttachmentFileName = (fileName) => {
  const normalized = String(fileName || "attachment")
    .replace(/[^\w.\-() ]/g, "_")
    .replace(/\s+/g, "_")
    .slice(0, 120);

  return normalized || "attachment";
};

export const getLocalAttachmentFileKey = (file) =>
  `${file.name}-${file.size}-${file.lastModified}`;

export const formatAttachmentSize = (size) => {
  const bytes = Number(size);

  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

export const validateAttachmentFiles = ({
  nextFiles,
  currentFiles = [],
  maxFiles,
  maxFileSize,
  maxTotalSize,
  rejectDuplicates = false,
}) => {
  const accepted = [];
  const rejected = [];
  const currentTotalSize = currentFiles.reduce(
    (total, file) => total + (Number(file?.size) || 0),
    0,
  );
  const seenFileKeys = rejectDuplicates
    ? new Set(currentFiles.map(getLocalAttachmentFileKey))
    : null;

  for (const file of nextFiles) {
    if (!file) continue;
    const fileKey = getLocalAttachmentFileKey(file);

    if (seenFileKeys) {
      if (seenFileKeys.has(fileKey)) {
        rejected.push(`${file.name} skipped: already attached`);
        continue;
      }
    }

    if (currentFiles.length + accepted.length >= maxFiles) {
      rejected.push(`${file.name} skipped: max ${maxFiles} files`);
      continue;
    }

    if (file.size > maxFileSize) {
      rejected.push(
        `${file.name} skipped: max file size is ${formatAttachmentSize(
          maxFileSize,
        )}`,
      );
      continue;
    }

    const acceptedTotalSize = accepted.reduce(
      (total, acceptedFile) => total + acceptedFile.size,
      0,
    );
    const nextTotalSize = currentTotalSize + acceptedTotalSize + file.size;

    if (maxTotalSize && nextTotalSize > maxTotalSize) {
      rejected.push(
        `${file.name} skipped: max total attachment size is ${formatAttachmentSize(
          maxTotalSize,
        )}`,
      );
      continue;
    }

    accepted.push(file);
    seenFileKeys?.add(fileKey);
  }

  return { accepted, rejected };
};

export const uploadStorageAttachment = ({
  file,
  user,
  buildStoragePath,
  customMetadata,
  onProgress,
}) =>
  new Promise((resolve, reject) => {
    const attachmentId = uuidv4();
    const safeFileName = normalizeAttachmentFileName(file.name);
    const storagePath = buildStoragePath({
      attachmentId,
      safeFileName,
      file,
      user,
    });
    const storageRef = ref(StorageDB, storagePath);
    const uploadTask = uploadBytesResumable(storageRef, file, {
      contentType: file.type || "application/octet-stream",
      customMetadata: {
        ...customMetadata,
        uploadedByUid: user.uid,
        uploadedByEmail: user.email || "",
      },
    });

    uploadTask.on(
      "state_changed",
      (snapshot) => {
        if (!onProgress) return;

        const progress = Math.round(
          (snapshot.bytesTransferred / snapshot.totalBytes) * 100,
        );
        onProgress(file, progress);
      },
      reject,
      () => {
        resolve({
          id: attachmentId,
          name: file.name,
          size: file.size,
          contentType: file.type || "application/octet-stream",
          storagePath,
          uploadedByUid: user.uid,
          uploadedByEmail: user.email || null,
          uploadedAt: new Date().toISOString(),
        });
      },
    );
  });

export const deleteStorageAttachments = async (attachments = []) => {
  const storagePaths = attachments
    .map((attachment) => attachment?.storagePath)
    .filter(Boolean);

  if (!storagePaths.length) return;

  await Promise.allSettled(
    storagePaths.map((storagePath) =>
      deleteObject(ref(StorageDB, storagePath)),
    ),
  );
};
